import {
  createContext,
  useContext,
  useCallback,
  useEffect,
  useRef,
  useState,
  ReactNode,
} from 'react'
import { nanoid } from 'nanoid'
import { useLocalStorage } from './useLocalStorage'
import { useHistory } from './useHistory'
import type {
  CV,
  PersonalInfo,
  Experience,
  Education,
  Skills,
  Language,
  Certification,
  Project,
  CVSettings,
} from '../types/cv'

const STORAGE_KEY = 'cvtoday_cv'
const ACTIVE_CV_KEY = 'cvtoday_active_cv_id'
const DEBOUNCE_MS = 1000

interface CVContextValue {
  cv: CV
  isDirty: boolean
  lastSaved: Date | null
  activeCVId: string | null
  updatePersonalInfo: (info: Partial<PersonalInfo>) => void
  updateSummary: (summary: string) => void
  addExperience: () => void
  updateExperience: (id: string, data: Partial<Experience>) => void
  removeExperience: (id: string) => void
  setExperience: (experience: Experience[]) => void
  addEducation: () => void
  updateEducation: (id: string, data: Partial<Education>) => void
  removeEducation: (id: string) => void
  setEducation: (education: Education[]) => void
  updateSkills: (skills: Partial<Skills>) => void
  addLanguage: () => void
  updateLanguage: (id: string, data: Partial<Language>) => void
  removeLanguage: (id: string) => void
  setLanguages: (languages: Language[]) => void
  addCertification: () => void
  updateCertification: (id: string, data: Partial<Certification>) => void
  removeCertification: (id: string) => void
  addProject: () => void
  updateProject: (id: string, data: Partial<Project>) => void
  removeProject: (id: string) => void
  updateSettings: (settings: Partial<CVSettings>) => void
  reorderExperience: (oldIndex: number, newIndex: number) => void
  reorderEducation: (oldIndex: number, newIndex: number) => void
  resetCV: () => void
  loadCV: (cvId: string, cvData: CV) => void
  createNewCV: () => string
  setActiveCVId: (id: string | null) => void
  undo: () => void
  redo: () => void
  canUndo: () => boolean
  canRedo: () => boolean
}

const CVContext = createContext<CVContextValue | null>(null)

const initialCV: CV = {
  personalInfo: {
    fullName: '',
    title: '',
    email: '',
    phone: '',
    location: '',
    links: [],
  },
  summary: '',
  experience: [],
  education: [],
  skills: {
    technical: [],
    soft: [],
    tools: [],
  },
  languages: [],
  certifications: [],
  projects: [],
  settings: {
    dateFormat: 'month-year',
  },
}

export function CVProvider({ children }: { children: ReactNode }) {
  const [savedCV, setSavedCV] = useLocalStorage<CV>(STORAGE_KEY, initialCV)
  const [cv, setCV] = useState<CV>(savedCV)
  const [isDirty, setIsDirty] = useState(false)
  const [lastSaved, setLastSaved] = useState<Date | null>(null)
  const [activeCVId, setActiveCVIdState] = useState<string | null>(() => {
    if (typeof window === 'undefined') return null
    return localStorage.getItem(ACTIVE_CV_KEY)
  })
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const history = useHistory()

  // Push history snapshots when CV changes (debounced by useHistory)
  const prevCVRef = useRef<string>('')
  useEffect(() => {
    const serialized = JSON.stringify(cv)
    if (prevCVRef.current && prevCVRef.current !== serialized) {
      history.pushSnapshot(JSON.parse(prevCVRef.current))
    }
    prevCVRef.current = serialized
  }, [cv, history])

  // Auto-save with debounce
  useEffect(() => {
    if (!isDirty) return

    if (debounceRef.current) {
      clearTimeout(debounceRef.current)
    }

    debounceRef.current = setTimeout(() => {
      setSavedCV(cv)
      setIsDirty(false)
      setLastSaved(new Date())
    }, DEBOUNCE_MS)

    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current)
      }
    }
  }, [cv, isDirty, setSavedCV])

  const updateCV = useCallback((updater: (prev: CV) => CV) => {
    setCV(updater)
    setIsDirty(true)
  }, [])

  const updatePersonalInfo = useCallback(
    (info: Partial<PersonalInfo>) => {
      updateCV((prev) => ({
        ...prev,
        personalInfo: { ...prev.personalInfo, ...info },
      }))
    },
    [updateCV]
  )

  const updateSummary = useCallback(
    (summary: string) => {
      updateCV((prev) => ({ ...prev, summary }))
    },
    [updateCV]
  )

  const addExperience = useCallback(() => {
    updateCV((prev) => ({
      ...prev,
      experience: [
        ...(prev.experience || []),
        { id: nanoid(), current: false },
      ],
    }))
  }, [updateCV])

  const updateExperience = useCallback(
    (id: string, data: Partial<Experience>) => {
      updateCV((prev) => ({
        ...prev,
        experience: (prev.experience || []).map((exp) =>
          exp.id === id ? { ...exp, ...data } : exp
        ),
      }))
    },
    [updateCV]
  )

  const removeExperience = useCallback(
    (id: string) => {
      updateCV((prev) => ({
        ...prev,
        experience: (prev.experience || []).filter((exp) => exp.id !== id),
      }))
    },
    [updateCV]
  )

  const setExperience = useCallback(
    (experience: Experience[]) => {
      updateCV((prev) => ({ ...prev, experience }))
    },
    [updateCV]
  )

  const addEducation = useCallback(() => {
    updateCV((prev) => ({
      ...prev,
      education: [
        ...(prev.education || []),
        { id: nanoid(), current: false },
      ],
    }))
  }, [updateCV])

  const updateEducation = useCallback(
    (id: string, data: Partial<Education>) => {
      updateCV((prev) => ({
        ...prev,
        education: (prev.education || []).map((edu) =>
          edu.id === id ? { ...edu, ...data } : edu
        ),
      }))
    },
    [updateCV]
  )

  const removeEducation = useCallback(
    (id: string) => {
      updateCV((prev) => ({
        ...prev,
        education: (prev.education || []).filter((edu) => edu.id !== id),
      }))
    },
    [updateCV]
  )

  const setEducation = useCallback(
    (education: Education[]) => {
      updateCV((prev) => ({ ...prev, education }))
    },
    [updateCV]
  )

  const updateSkills = useCallback(
    (skills: Partial<Skills>) => {
      updateCV((prev) => ({
        ...prev,
        skills: { ...prev.skills, ...skills },
      }))
    },
    [updateCV]
  )

  const addLanguage = useCallback(() => {
    updateCV((prev) => ({
      ...prev,
      languages: [...(prev.languages || []), { id: nanoid() }],
    }))
  }, [updateCV])

  const updateLanguage = useCallback(
    (id: string, data: Partial<Language>) => {
      updateCV((prev) => ({
        ...prev,
        languages: (prev.languages || []).map((lang) =>
          lang.id === id ? { ...lang, ...data } : lang
        ),
      }))
    },
    [updateCV]
  )

  const removeLanguage = useCallback(
    (id: string) => {
      updateCV((prev) => ({
        ...prev,
        languages: (prev.languages || []).filter((lang) => lang.id !== id),
      }))
    },
    [updateCV]
  )

  const setLanguages = useCallback(
    (languages: Language[]) => {
      updateCV((prev) => ({ ...prev, languages }))
    },
    [updateCV]
  )

  const addCertification = useCallback(() => {
    updateCV((prev) => ({
      ...prev,
      certifications: [...(prev.certifications || []), { id: nanoid() }],
    }))
  }, [updateCV])

  const updateCertification = useCallback(
    (id: string, data: Partial<Certification>) => {
      updateCV((prev) => ({
        ...prev,
        certifications: (prev.certifications || []).map((cert) =>
          cert.id === id ? { ...cert, ...data } : cert
        ),
      }))
    },
    [updateCV]
  )

  const removeCertification = useCallback(
    (id: string) => {
      updateCV((prev) => ({
        ...prev,
        certifications: (prev.certifications || []).filter(
          (cert) => cert.id !== id
        ),
      }))
    },
    [updateCV]
  )

  const addProject = useCallback(() => {
    updateCV((prev) => ({
      ...prev,
      projects: [...(prev.projects || []), { id: nanoid() }],
    }))
  }, [updateCV])

  const updateProject = useCallback(
    (id: string, data: Partial<Project>) => {
      updateCV((prev) => ({
        ...prev,
        projects: (prev.projects || []).map((proj) =>
          proj.id === id ? { ...proj, ...data } : proj
        ),
      }))
    },
    [updateCV]
  )

  const removeProject = useCallback(
    (id: string) => {
      updateCV((prev) => ({
        ...prev,
        projects: (prev.projects || []).filter((proj) => proj.id !== id),
      }))
    },
    [updateCV]
  )

  const updateSettings = useCallback(
    (settings: Partial<CVSettings>) => {
      updateCV((prev) => ({
        ...prev,
        settings: { ...prev.settings, ...settings },
      }))
    },
    [updateCV]
  )

  const resetCV = useCallback(() => {
    setCV(initialCV)
    setSavedCV(initialCV)
    setIsDirty(false)
    setLastSaved(null)
  }, [setSavedCV])

  const setActiveCVId = useCallback((id: string | null) => {
    setActiveCVIdState(id)
    if (typeof window !== 'undefined') {
      if (id) {
        localStorage.setItem(ACTIVE_CV_KEY, id)
      } else {
        localStorage.removeItem(ACTIVE_CV_KEY)
      }
    }
  }, [])

  const loadCV = useCallback((cvId: string, cvData: CV) => {
    setCV(cvData)
    setSavedCV(cvData)
    setActiveCVId(cvId)
    setIsDirty(false)
    setLastSaved(null)
  }, [setSavedCV, setActiveCVId])

  const createNewCV = useCallback((): string => {
    const newId = nanoid()
    setCV(initialCV)
    setSavedCV(initialCV)
    setActiveCVId(newId)
    setIsDirty(false)
    setLastSaved(null)
    return newId
  }, [setSavedCV, setActiveCVId])

  const reorderExperience = useCallback(
    (oldIndex: number, newIndex: number) => {
      updateCV((prev) => {
        const items = [...(prev.experience || [])]
        const [moved] = items.splice(oldIndex, 1)
        items.splice(newIndex, 0, moved)
        return { ...prev, experience: items }
      })
    },
    [updateCV]
  )

  const reorderEducation = useCallback(
    (oldIndex: number, newIndex: number) => {
      updateCV((prev) => {
        const items = [...(prev.education || [])]
        const [moved] = items.splice(oldIndex, 1)
        items.splice(newIndex, 0, moved)
        return { ...prev, education: items }
      })
    },
    [updateCV]
  )

  const handleUndo = useCallback(() => {
    const previous = history.undo(cv)
    if (previous) {
      setCV(previous)
      setIsDirty(true)
      prevCVRef.current = JSON.stringify(previous) // prevent re-pushing to history
    }
  }, [cv, history])

  const handleRedo = useCallback(() => {
    const next = history.redo(cv)
    if (next) {
      setCV(next)
      setIsDirty(true)
      prevCVRef.current = JSON.stringify(next)
    }
  }, [cv, history])

  const value: CVContextValue = {
    cv,
    isDirty,
    lastSaved,
    activeCVId,
    updatePersonalInfo,
    updateSummary,
    addExperience,
    updateExperience,
    removeExperience,
    setExperience,
    addEducation,
    updateEducation,
    removeEducation,
    setEducation,
    updateSkills,
    addLanguage,
    updateLanguage,
    removeLanguage,
    setLanguages,
    addCertification,
    updateCertification,
    removeCertification,
    addProject,
    updateProject,
    removeProject,
    updateSettings,
    reorderExperience,
    reorderEducation,
    resetCV,
    loadCV,
    createNewCV,
    setActiveCVId,
    undo: handleUndo,
    redo: handleRedo,
    canUndo: history.canUndo,
    canRedo: history.canRedo,
  }

  return <CVContext.Provider value={value}>{children}</CVContext.Provider>
}

export function useCV(): CVContextValue {
  const context = useContext(CVContext)
  if (!context) {
    throw new Error('useCV must be used within a CVProvider')
  }
  return context
}
